import { Form, TextArea } from 'components/Form';
import styled, { css, rem, screenMobilePlusAndBigger, spacingBase, srOnly, tablet } from 'theme';

export const SearchForm = styled.form`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;

  ${tablet(css`
    width: calc(100% - ${rem(48)});
    max-width: calc(1200px - ${rem(48)});
  `)}
`;

export const SearchContainer = styled.div`
  position: absolute;
  width: 100%;
  height: ${rem(144)};
  display: flex;
  flex-direction: column;
  align-items: center;
  border-top: ${rem(1)} solid var(--ref-color-gray-80);
  background-color: var(--ref-color-blackwhite-white);
  padding: ${rem(20)} ${() => spacingBase};

  ${screenMobilePlusAndBigger(css`
    height: ${rem(124)};
  `)}

  &:after {
    box-shadow: inset 0 11px 8px -10px rgba(0, 0, 0, 0.3);
    content: '';
    display: block;
    height: 8px;
    margin-top: 8px;
    position: absolute;
    bottom: -8px;
    width: 100%;
    z-index: -1;
  }
`;

interface SearchInputProps {
  hasResults: boolean;
}

// `Highlight` is a predefined system color, see https://www.w3.org/TR/CSS2/ui.html
export const SearchInput = styled.div<SearchInputProps>`
  --a11y-outline-color: Highlight;

  // Webkit focus styling
  @supports (outline-color: -webkit-focus-ring-color) {
    --a11y-outline-color: -webkit-focus-ring-color;
  }

  // Firefox Focus styling
  @supports (outline-color: -moz-mac-focusring) {
    --a11y-outline-color: -moz-mac-focusring;
  }

  position: relative;
  z-index: 1;
  display: flex;
  flex: 1 0 0;
  flex-direction: row;
  align-items: center;
  align-self: stretch;
  justify-content: center;
  background-color: var(--ref-color-blackwhite-white);
  border: ${rem(1)} solid var(--ref-color-gray-80);
  border-radius: ${rem(8)};
  gap: ${rem(6)};
  padding: ${rem(6)};
  height: ${rem(48)};
  margin: ${rem(7)} 0;

  > input {
    border-radius: ${rem(4)};
  }
`;

export const SearchOutput = styled.output`
  position: relative;
  top: ${rem(-12)};
  width: 100%;

  ${tablet(css`
    width: calc(100% - ${rem(48)});
    max-width: calc(${rem(1200)} - ${rem(48)});
  `)}
`;

export const SearchResult = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${rem(6)} ${rem(16)} ${rem(16)};
  border: ${rem(1)} solid var(--ref-color-gray-80);
  border-radius: 0 0 ${rem(8)} ${rem(8)};
  border-top: none;
  background-color: var(--ref-color-blackwhite-white);
  box-shadow: 0 1px 24px rgba(0, 0, 0, 0.2);

  a {
    color: var(--ref-color-blackwhite-black);
    font-weight: var(--ref-font-weight-600);
    text-decoration: var(--ref-textdecoration-underline);
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

export const SearchFeedbackWrapper = styled.div`
  margin-block: ${rem(20)};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${rem(8)};

  p {
    font-size: ${rem(14)};
    font-weight: var(--ref-font-weight-600);
  }
`;

export const Disclaimer = styled.div`
  font-size: ${rem(14)};
  font-weight: var(--ref-font-weight-200);
  line-height: ${rem(16)};
  text-align: left;
  color: var(--ref-color-gray-70);

  a {
    color: currentColor;
    font-weight: normal;

    &:hover {
      text-decoration: none;
    }
  }
`;

export const Search = styled('input')`
  padding: ${rem(10)};
  width: 100%;
  height: 100%;
  border: none;
  outline: 2px auto var(--a11y-outline-color);
`;

interface SubmitButtonProps {
  active: boolean;
}

const Button = styled.button`
  display: block;
  width: ${rem(36)};
  height: ${rem(36)};
  padding: ${rem(8)};
  color: var(--ref-color-blackwhite-black);
  cursor: pointer;
  border: 1px solid transparent;
  border-radius: ${rem(8)};
  transition:
    background-color 150ms ease-in-out,
    border-color 150ms ease-in-out;

  svg {
    width: ${rem(18)};
    height: ${rem(18)};
    vertical-align: bottom;
  }
`;

export const SubmitButton = styled(Button)<SubmitButtonProps>`
  background-color: var(--ref-color-gray-20);
  ${(p) =>
    p.active &&
    css`
      background-color: var(--sys-color-primary);

      &:hover:enabled,
      &:focus:enabled {
        border-color: var(--sys-color-primary-low);
        background-color: var(--sys-color-primary-low);
      }
    `}
`;

export const ButtonIcon = styled(Button)`
  background-color: var(--ref-color-blackwhite-white);
  border-color: currentColor;

  &:hover:enabled,
  &:focus:enabled {
    border-color: var(--sys-color-primary-low);
    background-color: var(--sys-color-primary-low);
  }
`;

export enum ButtonVariant {
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}
interface FeedbackButtonProps {
  variant: ButtonVariant;
  active: boolean;
}

enum ColorOpacity {
  LIGHT = '10', // 10%
}

export const FeedbackButtonIcon = styled('button')<FeedbackButtonProps>`
  display: block;
  cursor: pointer;
  width: ${rem(44)};
  height: ${rem(44)};
  border-radius: ${rem(24)};
  padding: ${rem(8)};
  border: 1px solid transparent;
  color: ${(p) => {
    if (p.active) {
      return 'var(--ref-color-blackwhite-white)';
    }
    return p.variant === ButtonVariant.SUCCESS ? 'var(--sys-color-success)' : 'var(--sys-color-error)';
  }};
  background-color: ${(p) => {
    if (p.active) {
      return p.variant === ButtonVariant.SUCCESS ? 'var(--sys-color-success)' : 'var(--sys-color-error)';
    }
    return 'transparent';
  }};
  transition:
    background-color 150ms ease-in-out,
    border-color 150ms ease-in-out;

  &:active:enabled,
  &:hover:enabled,
  &:focus:enabled {
    background-color: ${(p) => {
      if (p.active) {
        return p.variant === ButtonVariant.SUCCESS ? 'var(--sys-color-success)' : 'var(--sys-color-error)';
      }

      return p.variant === ButtonVariant.SUCCESS
        ? `color-mix(in srgb, var(--sys-color-success) ${ColorOpacity.LIGHT}%, transparent)`
        : `color-mix(in srgb, var(--sys-color-error) ${ColorOpacity.LIGHT}%, transparent)`;
    }};
    border-color: ${(p) =>
      p.variant === ButtonVariant.SUCCESS ? 'var(--sys-color-success)' : 'var(--sys-color-error)'};
  }

  svg {
    width: ${rem(24)};
    height: ${rem(24)};
    fill: currentColor;
    vertical-align: bottom;
    transition: fill 150ms ease-in-out;
  }
`;

export const ExtendedFeedbackForm = styled(Form)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(6)};
  padding-bottom: ${rem(16)};
`;

export const SearchLabelFeedback = styled.label`
  color: var(--ref-color-gray-100);
  font-weight: var(--ref-font-weight-600);
  font-size: ${rem(14)};
`;

export const ScreenreaderOnly = styled.span`
  ${srOnly}
`;

export const SearchFeedbackSuccess = styled.div`
  margin: ${rem(16)} 0 ${rem(32)};
`;

export const SearchFeedback = styled(TextArea)`
  width: 100%;
  resize: none;
`;
